import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Plus, Loader2, MapPin, Phone as PhoneIcon, Package, Trash2,
  Check, X, Route as RouteIcon, ExternalLink, Calendar, TrendingUp,
  ChevronDown, ChevronUp, Navigation, Search, RotateCcw, Crosshair
} from "lucide-react";
import { api, clearToken, getToken } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import DeliveryMap from "../components/delivery/DeliveryMap";

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  customer_name: "",
  phone: "",
  quantity: 1,
  address: "",
  notes: "",
  order_date: todayISO(),
});

const DeliveryDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [route, setRoute] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [pickMode, setPickMode] = useState(false);
  const [pickedPosition, setPickedPosition] = useState(null); // {lat, lng}

  const loadAll = async () => {
    try {
      setLoading(true);
      const [ordersRes, statsRes] = await Promise.all([
        api.get("/orders"),
        api.get("/orders/stats/daily"),
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        navigate("/teslimat");
      } else {
        toast({ title: "Hata", description: "Veriler yüklenemedi." });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getToken()) {
      navigate("/teslimat");
      return;
    }
    (async () => {
      try {
        const v = await api.get("/auth/verify");
        if (v.data.role !== "delivery") {
          clearToken();
          navigate("/teslimat");
          return;
        }
        await loadAll();
      } catch {
        clearToken();
        navigate("/teslimat");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (dateFilter && o.order_date !== dateFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!o.customer_name.toLowerCase().includes(s) && !o.address.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [orders, statusFilter, dateFilter, search]);

  const totals = useMemo(() => {
    return filteredOrders.reduce(
      (acc, o) => {
        acc.count += 1;
        acc.quantity += o.quantity || 0;
        if (o.status === "delivered") acc.delivered += 1;
        else acc.pending += 1;
        return acc;
      },
      { count: 0, quantity: 0, delivered: 0, pending: 0 }
    );
  }, [filteredOrders]);

  const todayStats = stats.find((s) => s.date === todayISO());

  const submitOrder = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.quantity) {
      toast({ title: "Eksik bilgi", description: "Ad Soyad ve miktar zorunlu." });
      return;
    }
    if (!form.address && !pickedPosition) {
      toast({ title: "Konum eksik", description: "Adres girin veya haritadan konum seçin." });
      return;
    }
    try {
      setSaving(true);
      const payload = { ...form, quantity: Number(form.quantity) };
      if (pickedPosition) {
        payload.lat = pickedPosition.lat;
        payload.lng = pickedPosition.lng;
        if (!payload.address) payload.address = `Harita: ${pickedPosition.lat.toFixed(5)}, ${pickedPosition.lng.toFixed(5)}`;
      }
      await api.post("/orders", payload);
      toast({
        title: "Sipariş eklendi",
        description: pickedPosition ? "Konum haritadan alındı." : "Adres harita üzerinde konumlandırıldı.",
      });
      setForm(emptyForm());
      setPickedPosition(null);
      setPickMode(false);
      await loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast({
        title: "Adres bulunamadı",
        description: detail + " Alternatif: 'Haritadan Konum Seç' butonunu kullanın.",
      });
    } finally {
      setSaving(false);
    }
  };

  const startPickMode = () => {
    setPickMode(true);
    toast({ title: "Konum seçme aktif", description: "Haritada teslimat noktasına tıklayın." });
  };

  const cancelPick = () => {
    setPickMode(false);
    setPickedPosition(null);
  };

  const handleLocationPick = (pos) => {
    setPickedPosition(pos);
    setPickMode(false);
  };

  const toggleStatus = async (order) => {
    try {
      const newStatus = order.status === "delivered" ? "pending" : "delivered";
      await api.patch(`/orders/${order.id}`, { status: newStatus });
      await loadAll();
    } catch (err) {
      toast({ title: "Hata", description: err.message });
    }
  };

  const removeOrder = async (order) => {
    if (!window.confirm(`${order.customer_name} siparişi silinsin mi?`)) return;
    try {
      await api.delete(`/orders/${order.id}`);
      setSelectedIds((prev) => {
        const s = new Set(prev);
        s.delete(order.id);
        return s;
      });
      await loadAll();
    } catch (err) {
      toast({ title: "Hata", description: err.message });
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const buildRoute = async () => {
    if (selectedIds.size === 0) {
      toast({ title: "Seçim yok", description: "Rota oluşturmak için en az bir sipariş seçin." });
      return;
    }
    try {
      setOptimizing(true);
      const res = await api.post("/orders/optimize-route", { order_ids: Array.from(selectedIds) });
      setRoute(res.data);
      toast({ title: "Rota oluşturuldu", description: `${res.data.stop_count} durak, toplam ${res.data.total_distance_km} km.` });
    } catch (err) {
      toast({ title: "Hata", description: err?.response?.data?.detail || err.message });
    } finally {
      setOptimizing(false);
    }
  };

  const clearRoute = () => {
    setRoute(null);
    setSelectedIds(new Set());
  };

  const openGoogleMaps = (o) => {
    if (o.lat != null && o.lng != null) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${o.lat},${o.lng}`, "_blank", "noopener,noreferrer");
    }
  };

  const logout = () => {
    clearToken();
    navigate("/teslimat");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-[#b91c1c]" />
      </div>
    );
  }

  const routedOrderIds = new Set((route?.ordered_stops || []).map((o) => o.id));

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-[#0f172a] border-b border-slate-700 sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#b91c1c] text-white flex items-center justify-center">
              <Navigation className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-white leading-none">Aktav Yarka Teslimat</h1>
              <p className="text-xs text-white/60 mt-0.5">Rota & Sipariş Yönetimi</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-white/90 text-sm flex-wrap">
            {todayStats ? (
              <div className="flex items-center gap-4 bg-white/5 rounded-lg px-4 py-2 border border-white/10">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-white/50">Bugün</div>
                  <div className="font-bold">{todayStats.order_count} sipariş · {todayStats.total_quantity} adet</div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-white/50">Bugün henüz sipariş yok</div>
            )}
            <Button variant="ghost" size="sm" onClick={logout} className="gap-2 text-white/85 hover:text-white hover:bg-white/10">
              <LogOut className="w-4 h-4" /> Çıkış
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 py-5 grid grid-cols-1 xl:grid-cols-12 gap-4">
        {/* LEFT COLUMN */}
        <section className="xl:col-span-5 space-y-4">
          {/* Add order form */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <button onClick={() => setShowForm(!showForm)} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-2">
                <Plus className="w-4 h-4 text-[#b91c1c]" />
                <span className="font-bold text-slate-900">Yeni Sipariş Ekle</span>
              </div>
              {showForm ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showForm && (
              <form onSubmit={submitOrder} className="p-4 pt-0 space-y-3 border-t border-slate-100">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">Ad Soyad *</label>
                    <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Ahmet Yılmaz" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">Telefon</label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0555 555 55 55" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">Miktar (adet) *</label>
                    <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">Sipariş Tarihi</label>
                    <Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Adres {!pickedPosition && "*"}</label>
                  <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Mahalle, sokak, ilçe, il ... (örn: 'Erenler mah. Barış sok. No:5, Ferizli, Sakarya')" rows={2} />
                  <p className="text-[11px] text-slate-400 mt-1">
                    İlçe ve il yazın; adres harita üzerinde otomatik konumlandırılır.
                  </p>
                </div>
                {/* Manual map location picker */}
                <div className={`rounded-lg border p-3 ${pickedPosition ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                  <div className="flex items-start gap-2">
                    <Crosshair className={`w-4 h-4 mt-0.5 shrink-0 ${pickedPosition ? "text-emerald-600" : "text-amber-600"}`} />
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-slate-800">
                        {pickedPosition ? "Konum haritadan seçildi ✓" : "Adres bulunamıyorsa haritadan seçin"}
                      </div>
                      {pickedPosition ? (
                        <div className="text-[11px] text-slate-600 mt-0.5">
                          Enlem: {pickedPosition.lat.toFixed(5)}, Boylam: {pickedPosition.lng.toFixed(5)} · Marker'ı sürükleyerek düzeltebilirsiniz.
                        </div>
                      ) : (
                        <div className="text-[11px] text-slate-600 mt-0.5">Butona basıp harita üzerinde teslimat noktasına tıklayın.</div>
                      )}
                    </div>
                    {pickedPosition ? (
                      <Button type="button" size="sm" variant="ghost" onClick={cancelPick} className="h-7 text-xs gap-1 text-red-600">
                        <X className="w-3 h-3" /> Kaldır
                      </Button>
                    ) : (
                      <Button type="button" size="sm" variant="outline" onClick={startPickMode} disabled={pickMode} className="h-7 text-xs gap-1 border-amber-300 text-amber-800 hover:bg-amber-100">
                        <MapPin className="w-3 h-3" /> {pickMode ? "Bekliyor..." : "Haritadan Seç"}
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Notlar</label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Teslimat notu, ekstra bilgi vb." rows={2} />
                </div>
                <Button type="submit" disabled={saving} className="w-full bg-[#b91c1c] hover:bg-[#991b1b] text-white gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {saving ? "Konumlandırılıyor..." : "Siparişi Ekle"}
                </Button>
              </form>
            )}
          </div>

          {/* Filters + Stats */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ad veya adres ara..." className="pl-9 h-9" />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white">
                <option value="all">Tüm Durumlar</option>
                <option value="pending">Bekleyen</option>
                <option value="delivered">Teslim Edilen</option>
              </select>
              <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="h-9 w-40" />
              {(dateFilter || statusFilter !== "all" || search) && (
                <Button size="sm" variant="ghost" onClick={() => { setDateFilter(""); setStatusFilter("all"); setSearch(""); }} className="h-9 gap-1">
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="text-xl font-black text-[#0f172a]">{totals.count}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Sipariş</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="text-xl font-black text-[#b91c1c]">{totals.quantity}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Toplam Adet</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="text-xl font-black text-amber-600">{totals.pending}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Bekliyor</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="text-xl font-black text-emerald-600">{totals.delivered}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Teslim</div>
              </div>
            </div>
          </div>

          {/* Route bar */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-slate-900 flex items-center gap-2"><RouteIcon className="w-4 h-4 text-[#b91c1c]" /> Rota Oluştur</div>
                <div className="text-xs text-slate-500 mt-0.5">Seçili: <b>{selectedIds.size}</b> sipariş • Başlangıç: Ferizli, Sakarya</div>
              </div>
              <div className="flex gap-2">
                {route && <Button size="sm" variant="outline" onClick={clearRoute}>Temizle</Button>}
                <Button size="sm" onClick={buildRoute} disabled={optimizing || selectedIds.size === 0} className="bg-[#0f172a] hover:bg-[#1e293b] text-white gap-2">
                  {optimizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RouteIcon className="w-4 h-4" />}
                  En Kısa Rota
                </Button>
              </div>
            </div>
            {route && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-xs text-slate-600">
                  <span>{route.stop_count} durak</span>
                  <span className="font-bold text-[#0f172a]">Toplam {route.total_distance_km} km</span>
                </div>
                <ol className="space-y-1.5 text-sm">
                  {route.ordered_stops.map((s, idx) => (
                    <li key={s.id} className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#b91c1c] text-white text-xs font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                      <span className="flex-1 truncate">{s.customer_name} — <span className="text-slate-500">{s.quantity} adet</span></span>
                      <span className="text-xs text-slate-400">+{s.distance_from_prev_km} km</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          {/* Orders list */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <span className="font-bold text-slate-900">Siparişler ({filteredOrders.length})</span>
              {selectedIds.size > 0 && <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="text-xs">Seçimi temizle</Button>}
            </div>
            <div className="divide-y divide-slate-100 max-h-[720px] overflow-y-auto">
              {filteredOrders.length === 0 && (
                <div className="p-8 text-center text-slate-400 text-sm">Sipariş bulunamadı.</div>
              )}
              {filteredOrders.map((o, idx) => {
                const inRoute = routedOrderIds.has(o.id);
                const routeIdx = route ? route.ordered_stops.findIndex((s) => s.id === o.id) : -1;
                return (
                  <div key={o.id} className={`p-3 hover:bg-slate-50 transition-colors cursor-pointer ${highlightId === o.id ? "bg-red-50" : ""}`} onClick={() => setHighlightId(o.id)}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(o.id)}
                        onChange={(e) => { e.stopPropagation(); toggleSelect(o.id); }}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 w-4 h-4 accent-[#b91c1c]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-[11px] font-bold flex items-center justify-center">{idx + 1}</span>
                          <span className="font-semibold text-slate-900 text-sm">{o.customer_name}</span>
                          {inRoute && routeIdx >= 0 && (
                            <Badge className="bg-[#b91c1c] text-white h-5 text-[10px]">Rota #{routeIdx + 1}</Badge>
                          )}
                          <Badge className={o.status === "delivered" ? "bg-emerald-100 text-emerald-800 h-5 text-[10px]" : "bg-amber-100 text-amber-800 h-5 text-[10px]"}>
                            {o.status === "delivered" ? "Teslim Edildi" : "Bekliyor"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <Calendar className="w-3 h-3" /> {o.order_date}
                          {o.phone && <><PhoneIcon className="w-3 h-3 ml-1" /> {o.phone}</>}
                          <span className="ml-auto bg-[#b91c1c]/10 text-[#b91c1c] font-bold px-2 py-0.5 rounded"><Package className="w-3 h-3 inline mr-1" />{o.quantity}</span>
                        </div>
                        <div className="text-xs text-slate-600 mt-1 flex items-start gap-1">
                          <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                          <span className="line-clamp-2">{o.address}</span>
                        </div>
                        {o.notes && <div className="text-[11px] text-slate-500 italic mt-1">"{o.notes}"</div>}
                        <div className="flex gap-1.5 mt-2">
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openGoogleMaps(o); }} className="h-7 text-xs gap-1 border-blue-200 text-blue-700 hover:bg-blue-50">
                            <ExternalLink className="w-3 h-3" /> Yol Tarifi
                          </Button>
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); toggleStatus(o); }} className={`h-7 text-xs gap-1 ${o.status === "delivered" ? "border-amber-200 text-amber-700" : "border-emerald-200 text-emerald-700"}`}>
                            {o.status === "delivered" ? <><X className="w-3 h-3" /> Bekliyor</> : <><Check className="w-3 h-3" /> Teslim</>}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); removeOrder(o); }} className="h-7 text-xs text-red-600 hover:bg-red-50 gap-1">
                            <Trash2 className="w-3 h-3" /> Sil
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Daily stats */}
          {stats.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-[#b91c1c]" />
                <span className="font-bold text-slate-900">Günlük İstatistik (son 60 gün)</span>
              </div>
              <div className="max-h-56 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 border-b border-slate-100">
                    <tr>
                      <th className="text-left py-2">Tarih</th>
                      <th className="text-right">Sipariş</th>
                      <th className="text-right">Adet</th>
                      <th className="text-right">Teslim</th>
                      <th className="text-right">Bekliyor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.map((s) => (
                      <tr key={s.date} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => setDateFilter(s.date)}>
                        <td className="py-2 text-slate-700">{s.date}</td>
                        <td className="text-right font-semibold">{s.order_count}</td>
                        <td className="text-right text-[#b91c1c] font-bold">{s.total_quantity}</td>
                        <td className="text-right text-emerald-600">{s.delivered}</td>
                        <td className="text-right text-amber-600">{s.pending}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {/* RIGHT COLUMN — MAP */}
        <section className="xl:col-span-7">
          <div className="bg-white rounded-xl border border-slate-200 p-2 sticky top-[80px] h-[calc(100vh-100px)] min-h-[600px]">
            <DeliveryMap
              orders={filteredOrders}
              route={route}
              highlightId={highlightId}
              pickMode={pickMode}
              pickedPosition={pickedPosition}
              onLocationPick={handleLocationPick}
            />
          </div>
        </section>
      </main>
    </div>
  );
};

export default DeliveryDashboard;
